import { useQueryClient } from "@tanstack/react-query"
import posthog from "posthog-js"
import { useEffect } from "react"
import { getSessionVersion } from "@/shared/lib/auth"
import { captureError } from "@/shared/lib/diagnostics"
import { onAppActive, useStoreId } from "@/shared/lib/platform"
import { isBillingConnectionLost } from "./errors"

/** Recover store transactions on login and resume (including Ask-to-Buy approvals). */
export function useRecoverUnfinishedTransactions(
	userId: number | undefined,
): void {
	const queryClient = useQueryClient()
	const store = useStoreId()

	useEffect(() => {
		if (!userId || !store) return

		let disposed = false
		const version = getSessionVersion()
		const run = (trigger: "launch" | "foreground") => {
			void import("./unfinished-transactions")
				.then(({ processUnfinishedTransactions }) => {
					if (disposed || version !== getSessionVersion()) return
					return processUnfinishedTransactions(
						queryClient,
						store,
						userId,
						version,
					)
				})
				.catch((error) => {
					if (disposed) return
					if (isBillingConnectionLost(error)) {
						posthog.capture("purchase_recovery_retry", { trigger, store })
						return
					}
					captureError(error, { scope: "purchase.unfinished", trigger })
				})
		}

		run("launch")
		const unsubscribe = onAppActive(() => run("foreground"))
		return () => {
			disposed = true
			unsubscribe()
		}
	}, [queryClient, store, userId])
}
